import {
  IQuestionDefinition,
  IQuestionFilterItem,
  IQuestionProperty,
  PropertyFilterItem,
} from '../constants/queryCreation.interface';

/**
 * Utility function to add a question-wide filter to pathVariables array,
 * preventing duplicates
 * @param arr string[]
 * @param value string
 * @returns void
 */
const addQuestionWideFilter = (arr: string[], value: string) => {
  const index = arr.indexOf(value);
  // prevent duplicates from being added
  if (index === -1) {
    arr.push(value);
  }
};

/**
 * Recursively traverses the filter tree and pushes dynamic filter values to the stack
 * @param filter IQuestionFilterItem | PropertyFilterItem
 * @param pushToStack function to push values to the stack
 * @returns void
 */
const traverseFilterTree = (
  filter: IQuestionFilterItem | PropertyFilterItem,
  pushToStack: (value: string) => void
) => {
  if (
    filter.conditionType === 'dynamic' &&
    filter.conditionTypeValue !== null &&
    filter.conditionTypeValue.length > 0
  ) {
    pushToStack(filter.conditionTypeValue);
  }

  if (filter.children && filter.children.length) {
    filter.children.forEach((item) => traverseFilterTree(item, pushToStack));
  }
};

/**
 * Processes properties & property aggregates of a question to extract dynamic filter values
 * and adds them to the pathVariables array
 * @param properties IQuestionProperty[]
 * @param pathVariables string[]
 * @returns void
 */
const processPropertyFilters = (properties: IQuestionProperty[], pathVariables: string[]) => {
  properties.forEach((prop) => {
    // property filters
    if (prop.filters && prop.filters.length) {
      prop.filters.forEach((filterGroup) =>
        traverseFilterTree(filterGroup, (value) => pathVariables.push(value))
      );
    }

    // aggregate filters
    if (prop.aggregates && prop.aggregates.length) {
      prop.aggregates.forEach((aggr) => {
        if (aggr.filters && aggr.filters.length) {
          aggr.filters.forEach((filterGroup) =>
            traverseFilterTree(filterGroup, (value) => pathVariables.push(value))
          );
        }
      });
    }
  });
};

/**
 * Processes the queryMap to extract dynamic filter values and adds them to the pathVariables array
 * @param queryMap IQuestionDefinition
 * @param pathVariables string[]
 * @returns void
 */
const processQuestionFilters = (queryMap: IQuestionDefinition = {}, pathVariables: string[]) => {
  Object.keys(queryMap).forEach((concept) => {
    // iterate through properties of each concept to find dynamic filters
    processPropertyFilters(queryMap[concept]?.properties || [], pathVariables);

    // find sub-queries and extract dynamic filter values
    if (queryMap[concept]?.subQueryDefinition) {
      Object.keys(queryMap[concept].subQueryDefinition).forEach((subQueryConcept) => {
        const subQuery = queryMap[concept].subQueryDefinition[subQueryConcept];
        // iterate through properties of each sub-query concept to find dynamic filters
        processPropertyFilters(subQuery.properties || [], pathVariables);
      });
    }
  });
};

/**
 * Processes question-wide filters and adds dynamic filter values to the pathVariables array
 * @param questionWideFilters IQuestionFilterItem[]
 * @param pathVariables string[]
 * @returns void
 */
const processQuestionWideFilters = (
  questionWideFilters: IQuestionFilterItem[] = [],
  pathVariables: string[]
) => {
  questionWideFilters.forEach((filterGroup) => {
    traverseFilterTree(filterGroup, (value) => addQuestionWideFilter(pathVariables, value));
  });
};

/**
 * Extracts dynamic filter values from the queryMap and question-wide filters
 * @param queryMap IQuestionDefinition
 * @param questionWideFilters IQuestionFilterItem[]
 * @returns string[] - array of dynamic filter values
 */
export const getPathVariables = (
  queryMap: IQuestionDefinition = {},
  questionWideFilters: IQuestionFilterItem[] = []
) => {
  let pathVariables: string[] = [];

  try {
    // process queryMap to extract dynamic filter values
    processQuestionFilters(queryMap, pathVariables);

    // process question-wide filters to extract dynamic filter values
    processQuestionWideFilters(questionWideFilters, pathVariables);
  } catch (e) {
    console.error(e);
    console.error('Error traversing filters in question definition OR question filters', {
      queryMap,
      questionWideFilters,
    });
  }

  return pathVariables;
};
